import type {TimelineItem} from "@/models/legislation/Timeline";
import { flattenTimeline } from "@/lib/diffViewHtml";
import {
  formatDanishDate,
  parseSignatureDate,
} from "@/lib/timelineDates";

/**
 * Branch point information for zoomed view labeling
 */
export type BranchPointInfo = {
  item: TimelineItem;
  date: Date;
  branches: TimelineItem[];
};

/**
 * Find a node in the tree by accession number
 */
export const findNode = (
  root: TimelineItem,
  accessionNumber: string
): TimelineItem | null => {
  if (root.accessionNumber === accessionNumber) return root;
  if (root.children) {
    for (const child of root.children) {
      const found = findNode(child, accessionNumber);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Find the ancestor path from root to a specific node (including the node itself)
 * Returns array from root to target node
 */
export const findAncestorPath = (
  root: TimelineItem,
  accessionNumber: string
): TimelineItem[] => {
  const path: TimelineItem[] = [];

  const search = (node: TimelineItem): boolean => {
    path.push(node);
    if (node.accessionNumber === accessionNumber) {
      return true;
    }
    if (node.children) {
      for (const child of node.children) {
        if (search(child)) {
          return true;
        }
      }
    }
    path.pop();
    return false;
  };

  search(root);
  return path;
};

/**
 * Get all unique dates from the entire tree
 */
export const getAllDatesFromTree = (root: TimelineItem): Date[] => {
  const dates: Date[] = [];
  const seen = new Set<number>();

  const collect = (node: TimelineItem) => {
    const date = parseSignatureDate(node.signatureDate);
    if (date) {
      const timestamp = date.getTime();
      if (!seen.has(timestamp)) {
        seen.add(timestamp);
        dates.push(date);
      }
    }
    node.children?.forEach(collect);
  };

  collect(root);
  dates.sort((a, b) => a.getTime() - b.getTime());
  return dates;
};

/**
 * Find the nearest earlier (or equal) date across ALL branches in the tree
 * Returns null if no date is found on or before targetDate
 */
export const findNearestEarlierDate = (
  root: TimelineItem,
  targetDate: Date
): Date | null => {
  const allDates = getAllDatesFromTree(root);
  const targetTime = targetDate.getTime();

  // Filter dates that are on or before target, then get the latest one
  const earlierDates = allDates.filter((d) => d.getTime() <= targetTime);
  if (earlierDates.length === 0) return null;

  return earlierDates[earlierDates.length - 1]; // Already sorted ascending
};

/**
 * Linear path used for date navigation: ancestors when going backward in time,
 * extended along the current branch when going forward. Forward traversal stops
 * at the next fork unless the fork is the current node—in that case it follows
 * `children[0]` (aligned with `getZoomLevelNodes`).
 */
export const getNavigationPathForDate = (
  root: TimelineItem,
  currentAccessionNumber: string,
  targetDate: Date
): TimelineItem[] => {
  const ancestorPath = findAncestorPath(root, currentAccessionNumber);
  if (ancestorPath.length === 0) return [];

  const currentNode = ancestorPath[ancestorPath.length - 1];
  const currentDate = parseSignatureDate(currentNode.signatureDate);
  const targetTime = targetDate.getTime();

  if (!currentDate || targetTime <= currentDate.getTime()) {
    return ancestorPath;
  }

  const path = [...ancestorPath];
  let node = currentNode;
  while (node.children && node.children.length > 0) {
    if (node.children.length > 1) {
      if (node.accessionNumber !== currentAccessionNumber) {
        break;
      }
      const child = node.children[0];
      path.push(child);
      const childDate = parseSignatureDate(child.signatureDate);
      if (childDate && childDate.getTime() > targetTime) break;
      node = child;
      continue;
    }

    const child = node.children[0];
    path.push(child);
    const childDate = parseSignatureDate(child.signatureDate);
    if (childDate && childDate.getTime() > targetTime) break;
    node = child;
  }

  return path;
};

/**
 * Find the nearest earlier (or equal) date on a linear path of timeline items.
 */
export const findNearestEarlierDateOnPath = (
  path: TimelineItem[],
  targetDate: Date
): Date | null => {
  const targetTime = targetDate.getTime();
  let nearest: Date | null = null;

  for (const node of path) {
    const nodeDate = parseSignatureDate(node.signatureDate);
    if (!nodeDate || nodeDate.getTime() > targetTime) continue;
    if (!nearest || nodeDate.getTime() > nearest.getTime()) {
      nearest = nodeDate;
    }
  }

  return nearest;
};

/**
 * Find documents on a linear path matching a specific date.
 */
export const findAllDocsForDateOnPath = (
  path: TimelineItem[],
  date: Date
): TimelineItem[] => {
  const targetTime = date.getTime();
  return path.filter((node) => {
    const nodeDate = parseSignatureDate(node.signatureDate);
    return nodeDate && nodeDate.getTime() === targetTime;
  });
};

/**
 * Find ALL documents in the tree matching a specific date
 */
export const findAllDocsForDate = (
  root: TimelineItem,
  date: Date
): TimelineItem[] => {
  const results: TimelineItem[] = [];
  const targetTime = date.getTime();

  const collect = (node: TimelineItem) => {
    const nodeDate = parseSignatureDate(node.signatureDate);
    if (nodeDate && nodeDate.getTime() === targetTime) {
      results.push(node);
    }
    node.children?.forEach(collect);
  };

  collect(root);
  return results;
};

/**
 * Check if the path from current document to a target date crosses any branch points
 * A branch point is where a node has multiple children
 */
export const doesPathCrossBranch = (
  root: TimelineItem,
  currentAccessionNumber: string,
  targetDate: Date
): boolean => {
  const currentPath = findAncestorPath(root, currentAccessionNumber);
  if (currentPath.length === 0) return false;

  const currentNode = currentPath[currentPath.length - 1];
  const currentDate = parseSignatureDate(currentNode.signatureDate);
  if (!currentDate) return false;

  const targetTime = targetDate.getTime();
  const currentTime = currentDate.getTime();

  if (targetTime < currentTime) {
    // Going backward - no branch crossing logic needed
    // When going back in time, if there's only one document at the target date,
    // there's no ambiguity (all branches converge to the same ancestor)
    // The zoom view will only show if multiple docs exist at that date
    return false;
  } else {
    // Going forward - check descendants for branches
    const hasBranchInDescendants = (node: TimelineItem): boolean => {
      if (node.children && node.children.length > 1) {
        // Check if any child is at or before target date
        for (const child of node.children) {
          const childDate = parseSignatureDate(child.signatureDate);
          if (childDate && childDate.getTime() <= targetTime) {
            return true;
          }
        }
      }
      if (node.children) {
        for (const child of node.children) {
          const childDate = parseSignatureDate(child.signatureDate);
          if (childDate && childDate.getTime() <= targetTime) {
            if (hasBranchInDescendants(child)) {
              return true;
            }
          }
        }
      }
      return false;
    };
    return hasBranchInDescendants(currentNode);
  }

  return false;
};

/**
 * Get branch points in the path between current document and target date
 */
export const getBranchPointsInPath = (
  root: TimelineItem,
  currentAccessionNumber: string,
  targetDate: Date
): BranchPointInfo[] => {
  const branchPoints: BranchPointInfo[] = [];
  const currentPath = findAncestorPath(root, currentAccessionNumber);
  if (currentPath.length === 0) return branchPoints;

  const currentNode = currentPath[currentPath.length - 1];
  const currentDate = parseSignatureDate(currentNode.signatureDate);
  if (!currentDate) return branchPoints;

  const targetTime = targetDate.getTime();
  const currentTime = currentDate.getTime();

  if (targetTime < currentTime) {
    // Going backward - collect branch points from ancestors
    for (let i = 0; i < currentPath.length - 1; i++) {
      const node = currentPath[i];
      const nodeDate = parseSignatureDate(node.signatureDate);
      if (nodeDate && nodeDate.getTime() >= targetTime) {
        if (node.children && node.children.length > 1) {
          branchPoints.push({
            item: node,
            date: nodeDate,
            branches: node.children,
          });
        }
      }
    }
  } else {
    // Going forward - collect branch points from descendants
    const collectBranches = (node: TimelineItem) => {
      const nodeDate = parseSignatureDate(node.signatureDate);
      if (node.children && node.children.length > 1 && nodeDate) {
        const relevantChildren = node.children.filter((child) => {
          const childDate = parseSignatureDate(child.signatureDate);
          return childDate && childDate.getTime() <= targetTime;
        });
        if (relevantChildren.length > 1) {
          branchPoints.push({
            item: node,
            date: nodeDate,
            branches: relevantChildren,
          });
        }
      }
      node.children?.forEach((child) => {
        const childDate = parseSignatureDate(child.signatureDate);
        if (childDate && childDate.getTime() <= targetTime) {
          collectBranches(child);
        }
      });
    };
    collectBranches(currentNode);
  }

  return branchPoints;
};

/**
 * Get surrounding nodes for the 7-point timeline strip view
 * Returns nodes on the same branch path (ancestors + current + descendants on main path)
 */
export const getSurroundingNodes = (
  root: TimelineItem,
  currentAccessionNumber: string,
  count: number = 3
): {
  before: TimelineItem[];
  current: TimelineItem | null;
  after: TimelineItem[];
} => {
  const ancestorPath = findAncestorPath(root, currentAccessionNumber);
  if (ancestorPath.length === 0) {
    return { before: [], current: null, after: [] };
  }

  const currentNode = ancestorPath[ancestorPath.length - 1];

  // Before: ancestors (excluding current), take last `count` items
  const before = ancestorPath.slice(0, -1).slice(-count);

  // After: descendants following "first child" path (main branch)
  const after: TimelineItem[] = [];
  let node = currentNode;
  while (after.length < count && node.children && node.children.length > 0) {
    // Follow first child (main branch)
    const firstChild = node.children[0];
    after.push(firstChild);
    node = firstChild;
  }

  return { before, current: currentNode, after };
};

/**
 * Get the full linear path (ancestors + current + descendants on main branch)
 */
const getFullLinearPath = (
  root: TimelineItem,
  currentAccessionNumber: string
): TimelineItem[] => {
  const ancestorPath = findAncestorPath(root, currentAccessionNumber);
  if (ancestorPath.length === 0) return [];

  const currentNode = ancestorPath[ancestorPath.length - 1];

  // Get all descendants following first child (main branch)
  const descendants: TimelineItem[] = [];
  let node = currentNode;
  while (node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    descendants.push(firstChild);
    node = firstChild;
  }

  // Full path: ancestors + current is already in ancestorPath, then add descendants
  return [...ancestorPath, ...descendants];
};

/**
 * Get surrounding nodes with smart filling - always shows `totalToShow` items when possible
 * Also returns counts of hidden items before and after
 */
export const getSurroundingNodesWithCounts = (
  root: TimelineItem,
  currentAccessionNumber: string,
  totalToShow: number = 7
): {
  items: TimelineItem[];
  hiddenBefore: number;
  hiddenAfter: number;
} => {
  const fullPath = getFullLinearPath(root, currentAccessionNumber);
  if (fullPath.length === 0) {
    return { items: [], hiddenBefore: 0, hiddenAfter: 0 };
  }

  // Find current index in full path
  const currentIndex = fullPath.findIndex(
    (item) => item.accessionNumber === currentAccessionNumber
  );
  if (currentIndex === -1) {
    return { items: [], hiddenBefore: 0, hiddenAfter: 0 };
  }

  const totalItems = fullPath.length;
  const itemsToShow = Math.min(totalToShow, totalItems);

  // Calculate ideal window centered around current
  let startIndex: number;
  let endIndex: number;

  if (itemsToShow >= totalItems) {
    // Show all items
    startIndex = 0;
    endIndex = totalItems;
  } else {
    // Calculate half-window size (excluding current)
    const halfWindow = Math.floor((itemsToShow - 1) / 2);

    // Start with centered window
    startIndex = currentIndex - halfWindow;
    endIndex = currentIndex + halfWindow + 1;

    // Adjust if window extends before start
    if (startIndex < 0) {
      endIndex = Math.min(endIndex - startIndex, totalItems);
      startIndex = 0;
    }

    // Adjust if window extends past end
    if (endIndex > totalItems) {
      startIndex = Math.max(startIndex - (endIndex - totalItems), 0);
      endIndex = totalItems;
    }

    // Ensure we show exactly itemsToShow items
    if (endIndex - startIndex < itemsToShow) {
      if (startIndex === 0) {
        endIndex = Math.min(itemsToShow, totalItems);
      } else {
        startIndex = Math.max(totalItems - itemsToShow, 0);
      }
    }
  }

  const items = fullPath.slice(startIndex, endIndex);
  const hiddenBefore = startIndex;
  const hiddenAfter = totalItems - endIndex;

  return { items, hiddenBefore, hiddenAfter };
};

/**
 * Check if there are more nodes before/after the visible window
 */
export const hasMoreNodes = (
  root: TimelineItem,
  currentAccessionNumber: string,
  count: number = 3
): { hasMoreBefore: boolean; hasMoreAfter: boolean } => {
  const ancestorPath = findAncestorPath(root, currentAccessionNumber);
  if (ancestorPath.length === 0) {
    return { hasMoreBefore: false, hasMoreAfter: false };
  }

  const hasMoreBefore = ancestorPath.length - 1 > count;

  // Check if there are more descendants beyond count
  const currentNode = ancestorPath[ancestorPath.length - 1];
  let depth = 0;
  let node = currentNode;
  while (node.children && node.children.length > 0) {
    depth++;
    node = node.children[0];
  }
  const hasMoreAfter = depth > count;

  return { hasMoreBefore, hasMoreAfter };
};

/**
 * Get the min and max dates from the tree for date-picker bounds
 */
export const getDateBounds = (
  root: TimelineItem
): { minDate: Date | null; maxDate: Date | null } => {
  const allDates = getAllDatesFromTree(root);
  if (allDates.length === 0) {
    return { minDate: null, maxDate: null };
  }
  return {
    minDate: allDates[0],
    maxDate: allDates[allDates.length - 1],
  };
};

/**
 * Clean title by replacing \n with proper line breaks or spaces
 */
export const cleanTitle = (
  title?: string,
  useSpaces: boolean = false
): string => {
  if (!title) return "Uden titel";
  return title.replace(/\\n/g, useSpaces ? " " : "\n");
};

/**
 * Get display label for a timeline item (date + shortName or title)
 */
export const getNodeLabel = (item: TimelineItem): string => {
  const date = parseSignatureDate(item.signatureDate);
  const dateStr = date ? formatDanishDate(date) : item.signatureDate;
  const name = item.shortName || cleanTitle(item.title, true);
  return `${dateStr}: ${name}`;
};

/**
 * Count all items in a subtree (including the root node)
 */
export const countSubtreeItems = (node: TimelineItem): number => {
  let count = 1;
  node.children?.forEach((child) => {
    count += countSubtreeItems(child);
  });
  return count;
};

/**
 * Branch information for zoom-level navigation
 */
export type BranchInfo = {
  firstItem: TimelineItem;
  itemCount: number;
  isCurrentPath: boolean;
};

/**
 * Result from getZoomLevelNodes - nodes for current zoom level plus branch point info
 */
export type ZoomLevelResult = {
  allNodes: TimelineItem[];
  currentIndex: number;
  branchPoint: {
    item: TimelineItem;
    branches: BranchInfo[];
  } | null;
};

/**
 * Check if a node or any of its descendants contains the target accession number
 */
export const subtreeContains = (
  node: TimelineItem,
  accessionNumber: string
): boolean => {
  if (node.accessionNumber === accessionNumber) return true;
  if (node.children) {
    for (const child of node.children) {
      if (subtreeContains(child, accessionNumber)) return true;
    }
  }
  return false;
};

/**
 * Get nodes for the current "zoom level" in hierarchical branch navigation.
 *
 * Returns the full linear path (ancestors + current + descendants up to the
 * next branch point). The caller is responsible for windowing / pagination.
 *
 * @param root - The root of the timeline tree
 * @param currentAccessionNumber - The currently viewed document's accession number
 */
export const getZoomLevelNodes = (
  root: TimelineItem,
  currentAccessionNumber: string,
  currentFlatTimelineIndex?: number | null
): ZoomLevelResult => {
  const ancestorPath = findAncestorPath(root, currentAccessionNumber);

  if (ancestorPath.length === 0) {
    return { allNodes: [], currentIndex: -1, branchPoint: null };
  }

  const allNodes: TimelineItem[] = [];

  const buildBranchPoint = (node: TimelineItem): ZoomLevelResult["branchPoint"] => {
    if (!node.children || node.children.length <= 1) return null;
    return {
      item: node,
      branches: node.children.map((child) => ({
        firstItem: child,
        itemCount: countSubtreeItems(child),
        isCurrentPath: subtreeContains(child, currentAccessionNumber),
      })),
    };
  };

  // Add ancestor path (root to current document)
  for (let i = 0; i < ancestorPath.length; i++) {
    const node = ancestorPath[i];
    allNodes.push(node);
  }

  // Continue FORWARD from the current document along descendants
  // until we hit the next branch point (unless current doc IS the fork — then
  // follow the primary child so the strip still shows later versions).
  let node = ancestorPath[ancestorPath.length - 1];

  while (node.children && node.children.length > 0) {
    if (node.children.length > 1) {
      if (node.accessionNumber !== currentAccessionNumber) {
        break;
      }
      const nextChild = node.children[0];
      if (!allNodes.includes(nextChild)) {
        allNodes.push(nextChild);
      }
      node = nextChild;
      continue;
    }

    const nextChild = node.children[0];
    if (!allNodes.includes(nextChild)) {
      allNodes.push(nextChild);
    }
    node = nextChild;
  }

  const flatTimeline = flattenTimeline([root]);
  const currentIndex = (() => {
    if (
      currentFlatTimelineIndex != null &&
      flatTimeline[currentFlatTimelineIndex] != null
    ) {
      const flatNode = flatTimeline[currentFlatTimelineIndex];
      const byIdentity = allNodes.findIndex((n) => n === flatNode);
      if (byIdentity >= 0) return byIdentity;

      const accession = flatNode.accessionNumber;
      if (accession) {
        const byAccession = allNodes.findIndex(
          (n) => n.accessionNumber === accession
        );
        if (byAccession >= 0) return byAccession;
      }
    }

    return allNodes.findIndex(
      (n) => n.accessionNumber === currentAccessionNumber
    );
  })();

  // Pick the fork on the visible strip closest to the current document (not the
  // earliest fork on the ancestor chain, which was always shown before).
  let branchPoint: ZoomLevelResult["branchPoint"] = null;
  let selectedForkIndex = -1;
  let selectedForkDistance = Infinity;
  allNodes.forEach((n, i) => {
    if (!n.children || n.children.length <= 1) return;
    const dist = Math.abs(i - currentIndex);
    if (
      dist < selectedForkDistance ||
      (dist === selectedForkDistance && i > selectedForkIndex)
    ) {
      selectedForkDistance = dist;
      selectedForkIndex = i;
      branchPoint = buildBranchPoint(n);
    }
  });

  return { allNodes, currentIndex, branchPoint };
};
